import { AutomationStatus, AutomationType } from "../../config/enums";
import { Time, When } from "./triggerTiming";
import { lookupRecordsCache } from "../../hooks/lookupRecords";
import { GetDefaultValuesForEntity } from "../../api/entityService";
import {
  PageRecord,
  PageSaveOption,
  RecordAdapterContext,
} from "../recordDefinition.types";

const isStatus = (record: PageRecord, status: number) =>
  String(record.status ?? "") === String(status);

const browserTimeZone = () =>
  (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) ||
  "Europe/London";

/** A new automation starts unpublished, with the defaults a triggered one is offered. */
const automationDefaults: PageRecord = {
  status: AutomationStatus.Unpublished,
  triggertype: "AbandonedBasket",
  triggerwhen: When.After,
  triggerinterval: 4,
  triggertimeunit: Time["Hour(s)"],
};

/** The defaults, plus the send window set in Communication Settings, which a triggered one keeps. */
export async function newAutomationRecord({
  dispatch,
}: RecordAdapterContext): Promise<PageRecord> {
  try {
    const response = await GetDefaultValuesForEntity(dispatch, "dwautomation");
    const { sendwindowstart, sendwindowend } = response?.data ?? {};

    return { ...automationDefaults, sendwindowstart, sendwindowend };
  } catch {
    return automationDefaults;
  }
}

// What the lookups were read from, as `lookupRecordsCache` holds them.
const nameOf = (cacheKey: string, id: unknown, fallback: unknown) =>
  lookupRecordsCache[cacheKey]?.find((option) => String(option.Key) === String(id))
    ?.Value ??
  fallback ??
  "";

/**
 * The attributes the service saves: every field the page holds, with the ones that
 * belong to the other type cleared, as the legacy form sent them, and the names of the
 * records the lookups point at.
 */
export function toAutomationPayload(record: PageRecord): PageRecord {
  const scheduled = String(record.type ?? "") === String(AutomationType.Scheduled);
  const triggered = String(record.type ?? "") === String(AutomationType.Triggered);
  const status = record.status ?? AutomationStatus.Unpublished;
  const running = isStatus(record, AutomationStatus.Published) ||
    isStatus(record, AutomationStatus.Testing);
  const keepIf = (kept: boolean, value: unknown) => (kept ? value ?? "" : "");

  return {
    name: record.name,
    campaignid: record.campaignid,
    campaignname: nameOf("campaign", record.campaignid, record.campaignidname),
    type: record.type,
    automationtype: record.type,
    status,
    ...(record.action ? { action: record.action } : {}),
    communicationid: record.communicationid,
    communicationname: nameOf(
      "g4c_communication:contactflow",
      record.communicationid,
      record.communicationidname
    ),
    audienceid: keepIf(scheduled, record.audienceid),
    audiencename: scheduled
      ? nameOf("g4c_communicationaudience", record.audienceid, record.audienceidname)
      : "",
    schedulemode: keepIf(scheduled, record.schedulemode),
    scheduledat: keepIf(scheduled, record.scheduledat),
    competitionid: keepIf(scheduled, record.competitionid),
    teamid: keepIf(scheduled, record.teamid),
    fixturerelativedirection: keepIf(scheduled, record.fixturerelativedirection),
    fixturerelativeoffset: keepIf(scheduled, record.fixturerelativeoffset),
    fixturerelativetime: keepIf(scheduled, record.fixturerelativetime),
    triggertype: keepIf(triggered, record.triggertype),
    triggerwhen: keepIf(triggered, record.triggerwhen),
    triggerinterval: keepIf(triggered, record.triggerinterval),
    triggertimeunit: keepIf(triggered, record.triggertimeunit),
    canbecancelled: keepIf(triggered, record.canbecancelled ?? false),
    sendwindowstart: keepIf(triggered, record.sendwindowstart),
    sendwindowend: keepIf(triggered, record.sendwindowend),
    scheduletimezoneid: scheduled && running ? browserTimeZone() : "",
  };
}

/** Save and Test sends only to the test marketing list; Save and Publish goes live. */
export const automationSaveMenu: PageSaveOption[] = [
  {
    id: "test",
    label: "Save and Test",
    description:
      "Only contacts in the automation test marketing list configured in Communication Settings will receive emails. Scheduled automations ignore the selected audience while testing.",
    patch: () => ({ status: AutomationStatus.Testing, action: "saveAndTest" }),
  },
  {
    id: "publish",
    label: "Save and Publish",
    patch: () => ({ status: AutomationStatus.Published, action: "saveAndPublish" }),
    confirmation: {
      title: "Save and publish automation",
      confirmText: "Save and Publish",
      paragraphs: [
        "Save these changes and publish this automation? Published automations can create executions and send communications to the configured audience.",
        "This action will save the automation as Published.",
      ],
    },
  },
];
